import { useEffect, useMemo, useState } from 'react';
import { productsApi } from '@/api/products.api';
import type { SelectOption } from '@/components/ui/Select';
import type { ProductResponse } from '@/types/product';
import { formatProductLabel, formatProductShort } from '@/utils/entityLabels';
import { showApiError } from '@/utils/apiError';

export function useProductOptions(pageSize = 500) {
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    productsApi
      .list({ page: 0, size: pageSize, activeOnly: true })
      .then((res) => setProducts(res.content))
      .catch((err) => showApiError(err, 'Failed to load products'))
      .finally(() => setLoading(false));
  }, [pageSize]);

  const options: SelectOption[] = useMemo(
    () => products.map((p) => ({ value: String(p.id), label: formatProductLabel(p) })),
    [products],
  );

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const getProduct = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined, short = false) => {
    const row = getProduct(id);
    if (!row) return id != null && id !== '' ? `#${id}` : '—';
    return short ? formatProductShort(row) : formatProductLabel(row);
  };

  return { products, options, loading, getProduct, getLabel, byId };
}
